import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { createSocket, ISocket } from '@/lib/socketAdapter';
import { API_CONFIG } from '@/lib/api';

/** Extract the other user's ID from a sorted DM room like "dm_aaa_bbb" */
function getOtherUserId(roomId: string, myId: string): string | null {
  if (!roomId?.startsWith('dm_')) return null;
  const parts = roomId.split('_');
  if (parts.length !== 3) return null;
  return parts[1] === myId ? parts[2] : parts[1];
}

interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'system';
  metadata?: any;
  status?: 'sent' | 'delivered' | 'read';
  created_at: string;
  updated_at?: string;
  /**
   * NULL when the sender's `users` row no longer exists — a message from a
   * removed teammate. The backend does this deliberately rather than dropping
   * the message: `ChatRepository.findMessagesByRoom` maps
   * `sender: senderMap[m.sender_id] || null`, and `findMessageWithSender` does
   * the same. This was typed as required, so nothing warned and ChatWindow
   * crashed the whole page on `msg.sender.avatar_url`. Keep it nullable so the
   * compiler catches any new unguarded access.
   */
  sender: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  } | null;
}

interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: string;
  is_online: boolean;
  last_seen: string;
}

interface ChatContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  socket: ISocket | null;

  // Messages
  messages: ChatMessage[];
  teamMembers: TeamMember[];

  // Current room
  currentRoom: string | null;

  // Current user
  currentUser: any;

  // Typing indicators — array of { userId, roomId } currently typing
  typingUsers: { userId: string; roomId: string }[];

  // Unread DM counts per member (memberId -> count)
  unreadCounts: Record<string, number>;

  // Last DM message per member (memberId -> message)
  lastMessages: Record<string, ChatMessage>;

  // Actions
  connect: () => void;
  disconnect: () => void;
  joinRoom: (roomId: string) => void;
  joinConversationRoom: (conversationId: string) => void;
  leaveRoom: (roomId: string) => void;
  sendMessage: (content: string, messageType?: string, metadata?: any) => void;
  sendDirectMessage: (recipientId: string, content: string, messageType?: string, metadata?: any) => void;
  forwardMessage: (targetRoomId: string, content: string, messageType?: string, metadata?: any) => Promise<boolean>;
  editMessage: (messageId: string, newContent: string) => void;
  deleteMessage: (messageId: string) => void;
  clearChat: (roomId: string) => Promise<boolean>;
  setTyping: (isTyping: boolean, roomId?: string) => void;
  markAsRead: (memberId: string) => void;
  markDmMessagesAsRead: (roomId: string) => void;

  // Loading states
  isLoadingMessages: boolean;
  isLoadingMembers: boolean;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<ISocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; roomId: string }[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [lastMessages, setLastMessages] = useState<Record<string, ChatMessage>>({});
  const typingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const teamMembersLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Connect to Socket.IO or AWS WebSocket
  const connect = useCallback(() => {
    if (socket || isConnecting) return;

    setIsConnecting(true);

    // START: Hybrid Socket Selection
    // 1. Try VITE_WEBSOCKET_URL (Serverless/AWS)
    // 2. Fallback to deriving from API Base URL (Local/Standard)
    const envSocketUrl = import.meta.env.VITE_WEBSOCKET_URL;
    const socketUrl = envSocketUrl || API_CONFIG.BASE_URL.replace('/api/v1', '');

    const newSocket = createSocket(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      // Adapter might not provide ID immediately for AWS, but standard ones do
      console.log('[ChatContext] Socket.IO connected! ID:', newSocket.id);
      if (newSocket.id) void(0);
      setIsConnected(true);
      setIsConnecting(false);

      // Authenticate with user ID
      if (user?.id) {
        console.log('[ChatContext] Authenticating with user ID:', user.id);
        newSocket.emit('authenticate', {
          userId: user.id
        });
      }
    });

    // ... (rest of listeners are mostly compatible, handled by adapter)

    newSocket.on('authenticated', (data) => {
      console.log('[ChatContext] Socket.IO authenticated:', data);

      // Join user room to receive conversations_updated events
      if (user?.id) {
        console.log('[ChatContext] Joining user room: user_' + user.id);
        newSocket.emit('join_room', { roomId: `user_${user.id}` });
      }

      // Join organization room to receive user_status (online/offline) broadcasts
      if ((user as any)?.organization_id) {
        const orgRoom = `organization_${(user as any).organization_id}`;
        console.log('[ChatContext] Joining organization room:', orgRoom);
        newSocket.emit('join_room', { roomId: orgRoom });
      }

      // Note: Team members are now loaded via REST API (serverless-compatible)
      // No need to request them via socket events
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      setIsConnecting(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setIsConnected(true);

      // Re-authenticate after reconnection
      if (user?.id) {
        newSocket.emit('authenticate', {
          userId: user.id
        });
      }
    });

    newSocket.on('reconnect_error', (error) => {
    });

    newSocket.on('reconnect_failed', () => {
      setIsConnected(false);
      setIsConnecting(false);
    });

    newSocket.on('message', (data) => {
      if (data.type === 'message' || data.type === 'direct_message') {
        const statusRank: Record<string, number> = { sent: 0, delivered: 1, read: 2 };
        setMessages(prev => {
          const existing = prev.find(m => m.id === data.data.id);
          if (existing) {
            // Update status if the incoming one is higher (e.g. delivered > sent)
            const incomingRank = statusRank[data.data.status] ?? 0;
            const existingRank = statusRank[existing.status] ?? 0;
            if (incomingRank > existingRank) {
              return prev.map(m => m.id === data.data.id ? { ...m, status: data.data.status } : m);
            }
            return prev;
          }
          return [...prev, data.data];
        });

        // Track DM metadata for sidebar badges and previews
        if (data.type === 'direct_message' && data.data.room_id) {
          const otherId = getOtherUserId(data.data.room_id, user?.id || '');
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: data.data }));
            // Increment unread only for messages from the other person
            if (data.data.sender_id !== user?.id) {
              setUnreadCounts(prev => ({
                ...prev,
                [otherId]: (prev[otherId] || 0) + 1
              }));
            }
          }
        }

        // Track team chat room unread counts
        if (data.type === 'message' && data.data.room_id && !data.data.room_id.startsWith('dm_')) {
          if (data.data.sender_id !== user?.id) {
            setUnreadCounts(prev => ({
              ...prev,
              [data.data.room_id]: (prev[data.data.room_id] || 0) + 1
            }));
          }
        }
      }
    });

    newSocket.on('chat_history', (data) => {
      // Merge: replace only this room's messages, keep others intact
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.room_id !== data.roomId);
        return [...filtered, ...data.messages];
      });

      // Seed lastMessages for DM rooms loaded via history
      if (data.roomId?.startsWith('dm_') && data.messages.length > 0) {
        const otherId = getOtherUserId(data.roomId, user?.id || '');
        if (otherId) {
          setLastMessages(prev => ({
            ...prev,
            [otherId]: data.messages[data.messages.length - 1]
          }));
        }
      }
    });

    newSocket.on('message_updated', (data) => {
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, content: data.newContent, updated_at: data.updatedAt }
          : msg
      ));
    });

    newSocket.on('message_deleted', (data) => {
      setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
    });

    // Handle message status updates (delivered / read)
    newSocket.on('messages_status_updated', (data) => {
      if (data.messageIds && data.status) {
        const idSet = new Set(data.messageIds);
        setMessages(prev => prev.map(msg =>
          idSet.has(msg.id) ? { ...msg, status: data.status } : msg
        ));
      }
    });

    // Note: team_members event listener removed - now using REST API for serverless compatibility

    newSocket.on('user_status', (data) => {
      setTeamMembers((prev) => {
        if (!prev || prev.length === 0) {
          return prev;
        }

        return prev.map((member) =>
          member.id === data.userId
            ? {
              ...member,
              is_online: data.is_online,
              last_seen: data.last_seen,
            }
            : member
        );
      });
    });

    newSocket.on('room_joined', (data) => {
      setCurrentRoom(data.roomId);
    });

    newSocket.on('room_left', (data) => {
      if (currentRoom === data.roomId) {
        setCurrentRoom(null);
      }
    });

    newSocket.on('user_typing', (data) => {
      const key = `${data.userId}_${data.roomId}`;

      // Clear any existing auto-clear timeout for this user+room
      if (typingTimeoutsRef.current[key]) {
        clearTimeout(typingTimeoutsRef.current[key]);
        delete typingTimeoutsRef.current[key];
      }

      if (data.isTyping) {
        setTypingUsers(prev => {
          if (prev.some(t => t.userId === data.userId && t.roomId === data.roomId)) return prev;
          return [...prev, { userId: data.userId, roomId: data.roomId }];
        });
        // Auto-clear after 3 s if no follow-up typing event arrives
        typingTimeoutsRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
          delete typingTimeoutsRef.current[key];
        }, 3000);
      } else {
        setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
      }
    });

    newSocket.on('error', (data) => {
    });

    // Listen for conversation updates (for WhatsApp messaging)
    newSocket.on('conversation_updated', (data) => {
      // This will be handled by the useConversationMessages hook
    });

    // Listen for conversations updates (for conversation list)
    newSocket.on('conversations_updated', (data) => {
      // This will be handled by the useRealTimeConversationsSocket hook
    });

    // Listen for messages marked as read
    newSocket.on('messages_marked_read', (data) => {
      // This will be handled by the useConversationMessages hook
    });

    setSocket(newSocket);
  }, [socket, isConnecting, user?.id, currentRoom]);

  // Disconnect from Socket.IO
  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  // Join a room - Load history via REST API for serverless compatibility
  const joinRoom = useCallback(async (roomId: string) => {
    console.log('[ChatContext] Joining room:', roomId);
    setCurrentRoom(roomId);
    
    // Load chat history via REST API (serverless-compatible)
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.getChatHistory(roomId, 50, 0);
      
      if (response.success && Array.isArray(response.data)) {
        console.log('[ChatContext] Chat history loaded:', response.data.length, 'messages');
        
        // Replace messages for this room
        setMessages(prev => {
          // Filter out old messages from this room
          const filtered = prev.filter(msg => msg.room_id !== roomId);
          // Add new messages from response.data
          return [...filtered, ...response.data];
        });
      }
    } catch (error) {
      console.error('[ChatContext] Error loading chat history:', error);
    }
    
    // Also emit socket event if connected (for real-time updates)
    if (socket && isConnected) {
      socket.emit('join_room', { roomId });
    }
  }, [socket, isConnected]);

  // Join conversation room to receive conversation_updated events
  const joinConversationRoom = useCallback((conversationId: string) => {
    if (socket && isConnected) {
      const roomId = `conversation_${conversationId}`;
      console.log('[ChatContext] Joining room:', roomId);
      socket.emit('join_room', { roomId });
    } else {
      console.warn('[ChatContext] Cannot join room - socket:', !!socket, 'isConnected:', isConnected);
    }
  }, [socket, isConnected]);

  // Leave a room
  const leaveRoom = useCallback((roomId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_room', {
        roomId
      });
    }
  }, [socket, isConnected]);

  // Send message - Serverless-compatible REST API approach
  const sendMessage = useCallback(async (content: string, messageType = 'text', metadata?: any) => {
    if (!currentRoom) {
      console.error('[ChatContext] Cannot send message - no room selected');
      return false;
    }

    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      console.log('[ChatContext] Sending message via REST API to room:', currentRoom);
      
      // Import ChatService dynamically
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      // Send message via REST API (serverless-compatible)
      const response = await ChatService.sendMessage({
        room_id: currentRoom,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        console.log('[ChatContext] Message sent successfully:', response.data);
        
        // Add message to local state immediately for instant UI update
        const messageData = response.data;
        setMessages(prev => {
          // Deduplicate - avoid adding if already exists
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        return true;
      } else {
        console.error('[ChatContext] Failed to send message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending message:', error);
      return false;
    }
  }, [currentRoom, user?.id]);

  // Send direct message - Serverless-compatible REST API approach
  const sendDirectMessage = useCallback(async (recipientId: string, content: string, messageType = 'text', metadata?: any) => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      console.log('[ChatContext] Sending direct message via REST API to:', recipientId);
      
      // Import ChatService dynamically
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      // Send message via REST API (serverless-compatible)
      const response = await ChatService.sendDirectMessage(user.id, {
        recipientId,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        console.log('[ChatContext] Direct message sent successfully:', response.data);

        // Add message to local state (or update if socket already added it)
        const messageData = response.data as any;
        setMessages(prev => {
          const existing = prev.find(m => m.id === messageData.id);
          if (existing) {
            return prev.map(m => m.id === messageData.id ? { ...m, ...messageData } : m);
          }
          return [...prev, messageData];
        });

        // Update last message for this DM
        setLastMessages(prev => ({
          ...prev,
          [recipientId]: messageData
        }));
        return true;
      } else {
        console.error('[ChatContext] Failed to send direct message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending direct message:', error);
      return false;
    }
  }, [user?.id]);

  // Forward an existing message's content to an arbitrary room (team room or DM).
  // Uses the same REST API as sendMessage but decouples the call from `currentRoom`
  // so you can forward from a DM to the team channel (or vice versa) without
  // swapping which room the UI is currently viewing.
  const forwardMessage = useCallback(async (
    targetRoomId: string,
    content: string,
    messageType = 'text',
    metadata?: any
  ): Promise<boolean> => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot forward message - user not authenticated');
      return false;
    }
    if (!targetRoomId) {
      console.error('[ChatContext] Cannot forward message - targetRoomId missing');
      return false;
    }
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.sendMessage({
        room_id: targetRoomId,
        content,
        message_type: messageType as any,
        metadata
      });
      if (response.success && response.data) {
        const messageData = response.data as any;
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        if (targetRoomId.startsWith('dm_')) {
          const otherId = getOtherUserId(targetRoomId, user.id);
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: messageData }));
          }
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('[ChatContext] Error forwarding message:', err);
      return false;
    }
  }, [user?.id]);

  // Set typing status — callers may pass an explicit roomId (e.g. DM windows)
  const setTyping = useCallback((isTyping: boolean, roomId?: string) => {
    const targetRoom = roomId || currentRoom;
    if (socket && isConnected && targetRoom) {
      socket.emit('typing', {
        roomId: targetRoom,
        isTyping
      });
    }
  }, [socket, isConnected, currentRoom]);

  // Clear unread count for a DM conversation when it is opened
  const markAsRead = useCallback((memberId: string) => {
    setUnreadCounts(prev => {
      if (prev[memberId]) {
        const { [memberId]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Edit message
  const editMessage = useCallback((messageId: string, newContent: string) => {
    if (socket && isConnected) {
      socket.emit('edit_message', {
        messageId,
        newContent
      });
    }
  }, [socket, isConnected]);

  // Delete message
  const deleteMessage = useCallback((messageId: string) => {
    if (socket && isConnected) {
      socket.emit('delete_message', {
        messageId
      });
    }
  }, [socket, isConnected]);

  // Mark all DM messages in a room as read (blue ticks)
  const markDmMessagesAsRead = useCallback((roomId: string) => {
    if (socket && isConnected && roomId) {
      socket.emit('mark_messages_read', { roomId });
    }
  }, [socket, isConnected]);

  // Clear all messages in a chat room
  const clearChat = useCallback(async (roomId: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;

      const res = await fetch(`${API_CONFIG.BASE_URL}/chat/clear/${encodeURIComponent(roomId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) return false;

      // Remove messages for this room from local state
      setMessages(prev => prev.filter(msg => msg.room_id !== roomId));

      // Clear last message for the other user in this DM
      if (roomId.startsWith('dm_')) {
        const parts = roomId.split('_');
        const otherUserId = parts[1] === user?.id ? parts[2] : parts[1];
        if (otherUserId) {
          setLastMessages(prev => {
            const { [otherUserId]: _, ...rest } = prev;
            return rest;
          });
        }
      }

      return true;
    } catch (error) {
      console.error('[ChatContext] Error clearing chat:', error);
      return false;
    }
  }, [user?.id]);

  // Load team members - Serverless-compatible REST API approach
  const loadTeamMembers = useCallback(async () => {
    console.log('[ChatContext] Loading team members via REST API...');
    setIsLoadingMembers(true);
    
    try {
      // Import TeamService dynamically to avoid circular dependencies
      const { TeamService } = await import('@/lib/api/services/teamService');
      
      // Fetch team members via REST API (serverless-compatible)
      const response = await TeamService.getTeamMembers({ limit: 100 });
      
      // Response structure: { success: boolean, data: TeamMember[] }
      if (response.success && Array.isArray(response.data)) {
        console.log('[ChatContext] Team members loaded successfully:', response.data.length);
        
        // Transform API response to match TeamMember interface
        const members = response.data.map((member: any) => ({
          id: member.id,
          email: member.email,
          full_name: member.name || member.full_name || member.email,
          avatar_url: member.avatar_url || null,
          role: member.role || 'USER',
          is_online: member.is_online || false,
          last_seen: member.last_seen || new Date().toISOString()
        }));
        
        setTeamMembers(members);
      } else {
        console.warn('[ChatContext] Failed to load team members:', response);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('[ChatContext] Error loading team members:', error);
      setTeamMembers([]);
    } finally {
      setIsLoadingMembers(false);
      
      // Clear timeout if it exists
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
        teamMembersLoadTimeoutRef.current = null;
      }
    }
  }, []);

  // Load team members when user is available (not dependent on socket connection)
  useEffect(() => {
    if (user?.id) {
      console.log('[ChatContext] Auto-loading team members via REST API...');
      loadTeamMembers();
    }
  }, [user?.id, loadTeamMembers]);

  // Auto-connect when user is available
  useEffect(() => {
    if (user?.id && !socket && !isConnecting) {
      connect();
    }
  }, [user?.id, socket, isConnecting, connect]);

  // NOTE: the social inbox room (`social-inbox-<orgId>`) is deliberately NOT
  // joined here. It carries customer DM bodies, and this context runs for every
  // logged-in member — including those whose `menu_permissions` revoke inbox
  // access. The mounted inbox surfaces join it themselves; see
  // backend/src/services/socialInboxRealtime.js.

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
      // Clear all pending typing-indicator auto-clear timeouts
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
      }
    };
  }, [socket]);

  const value: ChatContextType = {
    isConnected,
    isConnecting,
    socket,
    messages,
    teamMembers,
    currentRoom,
    currentUser: user,
    typingUsers,
    unreadCounts,
    lastMessages,
    connect,
    disconnect,
    joinRoom,
    joinConversationRoom,
    leaveRoom,
    sendMessage,
    sendDirectMessage,
    forwardMessage,
    editMessage,
    deleteMessage,
    clearChat,
    setTyping,
    markAsRead,
    markDmMessagesAsRead,
    isLoadingMessages,
    isLoadingMembers
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  // Return undefined instead of throwing - allows components to handle missing context gracefully
  // This is needed for admin routes where ChatProvider is not available
  return context;
}
